import { Component, computed, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import CountryList from 'country-list-with-dial-code-and-flag';
import { Country } from 'country-list-with-dial-code-and-flag/dist/country';
import { Select } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { InputMaskModule } from 'primeng/inputmask';
import { LoggingService } from '../../core/utils/logging.service';
import { LanguageConfigService } from '../language-config/language-config.service';
import { take } from 'rxjs';
import { IPreferences } from '../../core/services/dexie-indexed-db.service';
import { AsYouTypeFormatter, PhoneNumberFormat, PhoneNumberType, PhoneNumberUtil } from 'google-libphonenumber';
import { InputText } from 'primeng/inputtext';
import { TranslocoPipe } from '@jsverse/transloco';

export interface ICountryPhone {
  countryCodeISO2: string; // ISO2 like 'TH', 'US'
  countryCode?: string; // '+66', '+1'
  mobile: string; // '+66812345678'
}
export interface IPhoneChangeResult {
  valid: boolean;
  data: ICountryPhone | null;
}

@Component({
  selector: 'app-country-phone-select',
  imports: [Select, FormsModule, InputMaskModule, InputText, TranslocoPipe],
  templateUrl: './country-phone-select.component.html',
  styleUrl: './country-phone-select.component.scss',
})
export class CountryPhoneSelectComponent implements OnInit {
  @Input() set currentPhone(data: ICountryPhone | null) {
    this.hasCountryCodeInput.set(true);
    if (data) {
      this.loggingService.log('Received currentPhone input:', data);
      if (!data.countryCodeISO2) {
        this.setDefaultCountryFromLang();
      }

      // 🔥 Prevent infinite loop: Skip if the value hasn't changed
      const currentMobile = this.sanitize(this.phone());
      const incomingMobile = this.sanitize(data.mobile);

      if (this.currentCountryCodeSignal() === data.countryCodeISO2 && currentMobile === incomingMobile) {
        this.loggingService.log('Received currentPhone No Match:', data);
        this.currentCountryCodeSignal.set(data.countryCodeISO2);
        this.getSelectedCountry();
        return; // Same value, don't re-process
      }

      this.currentCountryCodeSignal.set(data.countryCodeISO2);
      this.loggingService.log('Received currentPhone Continue:', data);

      // Format the incoming phone number
      const formattedPhone = this.patchFormatPhone(data.mobile, data.countryCodeISO2);
      this.phone.set(formattedPhone);

      // 🔥 Don't emit when processing external input (prevents infinite loop)
      this.getSelectedCountry(false);
    }
  }

  @Output() phoneChange = new EventEmitter<IPhoneChangeResult>();

  // ========================
  // SERVICES
  // ========================
  private readonly loggingService = inject(LoggingService);
  private readonly langService = inject(LanguageConfigService);
  private readonly phoneUtil = PhoneNumberUtil.getInstance();

  // ========================
  // STATE
  // ========================
  countries = signal<Country[]>(CountryList.getAll());
  currentCountryCodeSignal = signal<string>('US');
  maxLength = signal<number>(14); // default fallback
  hasCountryCodeInput = signal<boolean>(false);
  selectedCountry = signal<Country | null>(null);

  phone = signal<string>('');
  isValid = signal<boolean>(true);
  placeholder = signal<string>('Enter phone number');

  // ========================
  // COMPUTE
  // ========================
  formatter = computed(() => {
    const country = this.selectedCountry();
    // ⚠️ NO SIDE EFFECTS IN COMPUTED - removed logging to prevent infinite loop
    return new AsYouTypeFormatter(country?.code || 'TH');
  });

  // ========================
  // INIT
  // ========================
  ngOnInit(): void {
    if (!this.hasCountryCodeInput()) {
      this.setDefaultCountryFromLang();
    }
  }

  // ========================
  // FORMAT INCOMING PHONE
  // ========================
  private patchFormatPhone(mobile: string, countryCode: string): string {
    if (!mobile) {
      return '';
    }

    const parsed = this.phoneUtil.parseAndKeepRawInput(mobile, countryCode);

    if (!parsed) {
      this.loggingService.warn('Failed to parse incoming phone:', mobile);
      return mobile;
    }

    const formatted = this.phoneUtil.format(parsed, PhoneNumberFormat.NATIONAL).replace(/\s/g, '-');
    return formatted || mobile;
  }

  // ========================
  // INIT LOGIC
  // ========================
  setDefaultCountryFromLang(): void {
    this.langService
      .getLangStateIdb()
      .pipe(take(1))
      .subscribe({
        next: (res: IPreferences | undefined) => {
          const countryCode = this.resolveCountryCodeFromLang(res?.language);
          this.currentCountryCodeSignal.set(countryCode);

          this.loggingService.log('Set default country from language:', res?.language);
          this.loggingService.log('Resolved country code:', countryCode);
          this.getSelectedCountry();
        },
        error: () => {
          this.currentCountryCodeSignal.set('US');
          this.getSelectedCountry();
        },
      });
  }

  resolveCountryCodeFromLang(lang?: string): string {
    return lang === 'th' ? 'TH' : 'US';
  }

  resolveCountryCodeLengthFromLang(lang?: string): string {
    return lang === 'th' ? '10' : '14';
  }

  // ========================
  // COUNTRY
  // ========================
  getSelectedCountry(shouldEmit = true) {
    this.loggingService.warn('\n===== getSelectedCountry=====');
    const code = this.currentCountryCodeSignal();
    const country = this.countries().find((c) => c.code === code) ?? null;
    this.loggingService.warn('\n===== getSelectedCountry=====', country);
    this.selectedCountry.set(country);

    if (country) {
      this.updatePlaceholder(country);
    }

    this.validatePhone(shouldEmit);
  }

  onCountryChange(country: any) {
    this.loggingService.warn('\n===== COUNTRY CHANGE =====');
    this.loggingService.warn('Country:', country.code);

    // 1. set country
    this.selectedCountry.set(country);

    // 2. update placeholder
    this.updatePlaceholder(country);

    // 3. 🔥 clear phone input
    this.phone.set('');

    // 4. reset validation state
    this.isValid.set(false); // better UX than false

    // 5. debug (keep your logs)
    this.onCountryChanges(country);
  }

  // ========================
  // COUNTRY DEBUG
  // ========================
  onCountryChanges(country: any) {
    this.loggingService.warn('\n===== COUNTRY CHANGE =====');
    this.loggingService.warn('Country:', country);
    this.loggingService.warn('ISO Code:', country.code);
    this.loggingService.warn('Dial Code:', country.dialCode);
    this.loggingService.warn('Dial Length:', country.length);

    // 🔥 MOBILE example (fix Thailand 02)
    let example = this.phoneUtil.getExampleNumberForType(country.code, PhoneNumberType.MOBILE);

    if (!example) {
      example = this.phoneUtil.getExampleNumber(country.code);
    }

    if (example) {
      const national = this.phoneUtil.format(example, PhoneNumberFormat.NATIONAL);

      // 🔥 LENGTH INFO
      const totalLength = national.length;

      this.loggingService.warn('👉 Format pattern hint:', national);
      this.loggingService.warn('Subscriber Length:', totalLength);
    }
  }

  // ========================
  // INPUT
  // ========================
  onPhoneInput(value: string) {
    let clean = value.replace(/[^\d+]/g, '');

    if (clean.includes('+')) {
      clean = '+' + clean.replace(/\+/g, '');
    }

    const digitsOnly = clean.replace(/\D/g, '');
    const max = this.maxLength();

    let finalDigits = digitsOnly;

    if (digitsOnly.length > max) {
      this.loggingService.warn('❌ Exceed max length:', max);
      finalDigits = digitsOnly.slice(0, max);
    }

    // 🔥 FORMAT HERE (THIS IS WHAT YOU WERE MISSING)
    const formatted = this.formatPhone(finalDigits);

    // 🔥 APPLY FORMATTED VALUE
    this.phone.set(formatted || finalDigits);

    this.validatePhone();
  }

  // ========================
  // FORMAT
  // ========================
  private formatPhone(value: string): string {
    const formatter = this.formatter();

    formatter.clear();

    const digitsOnly = value.replace(/\D/g, '');
    this.loggingService.warn('Digits only:', digitsOnly);

    let result = '';

    for (const char of digitsOnly) {
      result = formatter.inputDigit(char);
    }

    return result.replace(/\s/g, '-');
  }

  // ========================
  // PLACEHOLDER
  // ========================
  updatePlaceholder(country: any) {
    this.loggingService.warn('\n===== PLACEHOLDER UPDATE =====');

    let example = this.phoneUtil.getExampleNumberForType(country.code, PhoneNumberType.MOBILE);

    if (!example) {
      example = this.phoneUtil.getExampleNumber(country.code);
    }

    if (example) {
      const formatted = this.phoneUtil.format(example, PhoneNumberFormat.NATIONAL);

      this.loggingService.warn('Placeholder:', formatted);

      const lengthOfFormatted = formatted.length;
      this.maxLength.set(Number(lengthOfFormatted));
      this.loggingService.warn('Set max length to:', lengthOfFormatted);
      this.placeholder.set(`${formatted}`);
    }
    // else {
    //   this.placeholder.set('Enter phone number');
    // }
  }

  // ========================
  // VALIDATION (NO TRY/CATCH)
  // ========================
  validatePhone(shouldEmit = true) {
    const raw = this.phone();
    const country = this.selectedCountry();

    this.loggingService.warn('\n===== VALIDATION =====');
    this.loggingService.warn('Raw:', raw);

    if (!raw || !country) {
      this.isValid.set(false);

      if (shouldEmit) {
        this.phoneChange.emit({
          valid: true,
          data: null,
        });
      }
      return;
    }

    const phone = this.sanitize(raw);
    this.loggingService.warn('Sanitized:', phone);

    if (phone.length < 4) {
      if (shouldEmit) {
        this.phoneChange.emit({
          valid: false,
          data: null,
        });
      }
      return;
    }

    if ((phone.match(/\+/g)?.length ?? 0) > 1 || (phone.includes('+') && !phone.startsWith('+'))) {
      this.isValid.set(false);

      if (shouldEmit) {
        this.phoneChange.emit({
          valid: false,
          data: null,
        });
      }
      return;
    }

    const isPossibleString = this.phoneUtil.isPossibleNumberString(phone, country.code);

    this.loggingService.warn('isPossibleString:', isPossibleString);

    if (!isPossibleString) {
      this.isValid.set(false);

      if (shouldEmit) {
        this.phoneChange.emit({
          valid: false,
          data: null,
        });
      }
      return;
    }

    const parsed = this.phoneUtil.parse(phone, country.code);

    const isPossible = this.phoneUtil.isPossibleNumber(parsed);
    const isValid = this.phoneUtil.isValidNumberForRegion(parsed, country.code);

    if (!(isPossible && isValid)) {
      this.isValid.set(false);

      if (shouldEmit) {
        this.phoneChange.emit({
          valid: false,
          data: null,
        });
      }

      return;
    }

    this.loggingService.warn('isPossible:', isPossible);
    this.loggingService.warn('isValid:', isValid);

    const e164 = this.phoneUtil.format(parsed, PhoneNumberFormat.E164);
    this.loggingService.warn('E164:', e164);

    this.isValid.set(isPossible && isValid);

    if (shouldEmit) {
      this.phoneChange.emit({
        valid: true,
        data: {
          countryCodeISO2: country.code,
          countryCode: country.dial_code,
          mobile: e164,
        },
      });
    }
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const char = event.key;

    // allow digits
    if (/^\d$/.test(char)) return;

    // allow + only at first position
    if (char === '+' && this.phone().length === 0) return;

    // allow control keys
    if (['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'].includes(char)) return;

    // block everything else
    event.preventDefault();
  }

  // ========================
  // SANITIZE
  // ========================
  private sanitize(value: string): string {
    let v = value.replace(/[^\d+]/g, '');

    if (v.includes('+')) {
      v = '+' + v.replace(/\+/g, '');
    }

    return v;
  }
}
